import { DebugLogger } from '@iconik-io/core/src/debug_logger/debug_logger';

const logger = DebugLogger.getInstance('unified_player_sync_client');

export type WebSocketClientOptions = {
  /**
   * The WebSocket server URL to connect to
   */
  serverUrl: string;

  /**
   * System name used in the connection URL.
   */
  systemName:
    | 'iconik-test'
    | 'iconik-aws-test-eu'
    | 'iconik-qa'
    | 'iconik-eu'
    | 'iconik-us'
    | 'iconik-aws-eu'
    | 'iconik-aws-us'
    | string;

  /**
   * System domain identifier used in the connection URL.
   */
  systemDomainId: string;

  /**
   * Unique session identifier used in the connection URL
   */
  sessionId: string;

  /**
   * User identifier used in the connection URL
   */
  userId: string;

  /**
   * Callback function that handles incoming messages from the server
   * @param data
   */
  onMessage?: (data: string) => void;

  /**
   * Callback function that runs when the WebSocket connection is established
   */
  onOpen?: () => void;

  /**
   * Callback function that runs when the WebSocket connection is closed.
   */
  onClose?: () => void;

  /**
   * If true, creates a new instance even if one exists for the same serverUrl and sessionId.
   * Primarily used for testing purposes.
   */
  forceNew?: boolean;
};

/**
 * Lightweight WebSocket client. It handles connection,
 * message handling, and automatic reconnection.
 */
export class WebSocketClient {
  static #instances: Map<string, WebSocketClient> = new Map();
  #webSocket: WebSocket | undefined;
  #reconnectTimeout: ReturnType<typeof setTimeout> | undefined;
  #webSocketUrl: string;
  #sessionId: string;
  #reconnectionAttempts: number = 0;
  #reconnecting: boolean = false;
  #options: WebSocketClientOptions;

  /**
   * Connect to a websocket server, or return the existing instance if one already exists.
   * @param options - The options for the WebSocketClient.
   */
  static connect(options: WebSocketClientOptions): WebSocketClient {
    const key = `${options.serverUrl}_${options.sessionId}`;
    let instance = WebSocketClient.#instances.get(key);

    if (!instance || options.forceNew) {
      instance = new WebSocketClient(options);
      WebSocketClient.#instances.set(key, instance);
    }

    return instance;
  }

  /**
   * Constructs a new WebSocketClient instance. Not to be called directly
   * @param options - The options for the WebSocketClient.
   * @private
   */
  private constructor(options: WebSocketClientOptions) {
    this.#webSocketUrl = options.serverUrl;
    this.#sessionId = options.sessionId;
    this.#options = options;
    this.#initializeWebSocket();
  }

  #initializeWebSocket(): void {
    try {
      const url = new URL(`${this.#webSocketUrl}/sessions/${this.#sessionId}`);
      url.searchParams.set('sys', this.#options.systemName);
      url.searchParams.set('system_domain_id', this.#options.systemDomainId);
      url.searchParams.set('user_id', this.#options.userId);
      logger.debug('Creating WebSocket object with URL', url.toString());
      this.#webSocket = new WebSocket(url);
      this.#addInitialEventListeners(this.#webSocket);
    } catch {
      this.#reconnecting = false;
    }
  }

  #addInitialEventListeners(webSocket: WebSocket): void {
    webSocket.onmessage = this.#handleMessage.bind(this);
    webSocket.onopen = this.#handleOpen.bind(this);
    webSocket.onclose = this.#handleClose.bind(this);
    webSocket.onerror = this.#handleError.bind(this);
  }

  #handleError(event: Event): void {
    this.#handleClose();
    // biome-ignore lint/suspicious/noConsole: debug
    console.error('WebSocket error:', event);
  }

  #handleMessage(event: MessageEvent): void {
    this.#handleReconnectCommand(event);
    this.#options.onMessage?.(event.data);
  }

  /**
   * Handles a "reconnect" command from the sync server.
   * This command indicates a mismatched DC for the current sync session. The
   * client must reconnect to the host specified in the command data.
   *
   * The server sends a JSON object like this:
   * {"command":"reconnect","data":{"url":"eu.iconik.io"}}
   */
  #handleReconnectCommand(event: MessageEvent): void {
    const eventData = JSON.parse(event.data);
    if (eventData.command !== 'reconnect') {
      return;
    }

    logger.debug('Received reconnect command', eventData);

    const url = eventData.data.url;
    if (!url) {
      logger.debug('Got reconnect command but no URL, ignoring reconnect');
      return;
    }

    const urlParsed = new URL(this.#webSocketUrl);
    urlParsed.host = url;
    // Enforce secure protocol (wss) and default port. This is especially
    // important in dev environments where the page might be served over
    // HTTP with a custom port.
    urlParsed.protocol = 'wss';
    urlParsed.port = '';
    logger.debug(
      'Reconnect command changed WebSocket URL to',
      urlParsed.toString(),
    );
    this.#webSocketUrl = urlParsed.toString();
  }

  send(message: string): void {
    this.#webSocket?.send(message);
  }

  #handleOpen(): void {
    if (this.#reconnectTimeout) {
      clearTimeout(this.#reconnectTimeout);
      this.#reconnectTimeout = undefined;
    }
    this.#reconnecting = false;
    this.#reconnectionAttempts = 0;
    this.#options.onOpen?.();
  }

  #handleClose(): void {
    this.#reconnecting = false;
    this.#webSocket?.close();
    this.#webSocket = undefined;
    this.#options.onClose?.();

    globalThis.addEventListener('online', this.#attemptReconnect.bind(this), {
      once: true,
    });
    this.reconnect();
  }

  /**
   * Schedules a reconnect attempt. The delay is calculated
   * using the reconnectionDelay and reconnectionDelayMax options.
   * It is then randomized to prevent multiple clients from reconnecting at the same time.
   * @public
   * @param reconnectionDelay - The delay in milliseconds before first attempting to reconnect. Defaults to 1000.
   * @param reconnectionDelayMax - The maximum delay in milliseconds before attempting to reconnect. Defaults to 10000.
   */
  reconnect(
    reconnectionDelay: number = 1000,
    reconnectionDelayMax: number = 10000,
  ): void {
    // Calculate delay for reconnect
    const delay = Math.min(
      reconnectionDelay * 2 ** this.#reconnectionAttempts,
      reconnectionDelayMax,
    );
    const randomizedDelay = Math.round(delay * (1 + 0.5 * Math.random()));

    if (!this.#reconnectTimeout) {
      this.#reconnectTimeout = setTimeout(() => {
        this.#attemptReconnect();
      }, randomizedDelay);
    }
  }

  async #attemptReconnect(): Promise<void> {
    if (
      this.#reconnecting ||
      this.#webSocket?.readyState === 1 // Open
    ) {
      return;
    }
    this.#reconnecting = true;
    this.#reconnectionAttempts++;
    this.#reconnectTimeout = undefined;
    this.#initializeWebSocket();
    this.reconnect();
  }

  /**
   * Allows users to manually disconnect the WebSocket connection and stop all reconnection attempts
   * @public
   */
  disconnect(): void {
    if (this.#webSocket) {
      this.#webSocket.onclose = null;
      const key = `${this.#webSocketUrl}_${this.#sessionId}`;
      WebSocketClient.#instances.delete(key);
      this.#webSocket.onerror = null;
      this.#webSocket.close();
      this.#webSocket = undefined;
      this.#options.onClose?.();
    }
    if (this.#reconnectTimeout) {
      clearTimeout(this.#reconnectTimeout);
      this.#reconnectTimeout = undefined;
    }
  }
}
